import { ModelCategory } from '@paragon/contract-library';
import { getLayoutModelsWithPendingChanges } from '@paragon/layout-models';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { RootState } from '..';
import { getActiveComponentDesignGuid } from '../selectors/getActiveComponentDesignSelector';
import { getNavigationPanelSelectedTab } from '../selectors/navigationPanelSelectors';
import { getRightSidebarInfo } from '../selectors/rightToolbarSelectors';
import {
  collapseAllComponentPanelRows,
  collapseAllExplorerPanelRows,
  leftSidebarSet,
  rightToolBarShowSet,
} from '../slices/panelDataSlice';
import { ThunkActionVoid } from './thunkActions';

function getTopLevelRowIdentifier({ application: { openContext } }: RootState) {
  switch (openContext?.type) {
    case undefined:
      return 'componentDesignContainer';

    case 'job':
      return openContext.jobId.toString();

    case 'project':
      return openContext.projectGuid;

    case 'station':
    case 'schedule-station':
      return openContext.stationGuid;

    case 'shared-component-set':
      return openContext.sharedComponentSetLookupCode;

    default:
      assertExhaustiveSwitch(openContext);
  }
}

export const collapseAll = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();
  const selectedTab = getNavigationPanelSelectedTab(state);

  switch (selectedTab) {
    case 'layout':
      const layoutModels = Object.values(getLayoutModelsWithPendingChanges(state));
      const topLevelRowIdentifier = getTopLevelRowIdentifier(state);
      const componentDesignGuids = layoutModels
        .filter((model) => model.category === ModelCategory.ComponentDesign)
        .map((model) => model.guid);

      dispatch(collapseAllExplorerPanelRows({ componentDesignGuids, topLevelRowIdentifier }));

      break;

    case 'component':
      const componentDesignGuid = getActiveComponentDesignGuid(state);
      if (!componentDesignGuid) return;

      dispatch(collapseAllComponentPanelRows({ componentDesignGuid }));

      break;

    case null:
      return;

    default:
      assertExhaustiveSwitch(selectedTab);
  }
};

export const toggleLeftSidebar = (): ThunkActionVoid => (dispatch, getState) => {
  const newLeftSidebarState = (() => {
    const { leftSidebar } = getState().panelData;
    switch (leftSidebar) {
      case 'closed':
        return 'open';

      case 'open':
      case 'analysisHistory':
      case 'notifications':
        return 'closed';

      case 'default':
        return 'negateDefault';

      case 'negateDefault':
        return 'default';

      default:
        assertExhaustiveSwitch(leftSidebar);
    }
  })();
  dispatch(leftSidebarSet(newLeftSidebarState));
};

export const toggleRightSidebarIfPossible = (): ThunkActionVoid => (dispatch, getState) => {
  const rightSidebar = getRightSidebarInfo(getState());
  if (rightSidebar.mode !== 'none' && !rightSidebar.canBeClosed) return;

  dispatch(rightToolBarShowSet(rightSidebar.mode === 'none'));
};
